import React, { useState, useEffect, useRef } from 'react';

interface Industry {
  id: number;
  name: string;
}

interface IndustriesFilterDropdownProps {
  show: boolean;
  selectedIndustryIds: number[];
  onClose: () => void;
  onApply: (industryIds: number[]) => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
}

const industryOptions = [
  'Webサイト', 'モバイル', 'EC', 'エンタメ系アプリ', '業務系アプリ', '組み込み・制御', 'IoT', 'AI', 'DX',
  'FinTech', 'ゲーム', 'ソーシャルゲーム', 'コンシューマーゲーム', 'メーカー', 'Sier', '金融', '生命保険',
  '銀行', '証券', '広告', '医療・福祉', '通信', 'サービス', '公共', '官公庁', '小売', '物流', '製造'
];

const IndustriesFilterDropdown: React.FC<IndustriesFilterDropdownProps> = ({
  show,
  selectedIndustryIds,
  onClose,
  onApply,
  buttonRef
}) => {
  const [allIndustries, setAllIndustries] = useState<Industry[]>([]);
  const [currentSelection, setCurrentSelection] = useState<number[]>(selectedIndustryIds);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (show) {
      setCurrentSelection(selectedIndustryIds);
      // Fetch industries only when the modal is shown and data is not yet loaded
      if (allIndustries.length === 0) {
        fetch('/api/v1/industries')
          .then(response => response.json())
          .then(data => setAllIndustries(data))
          .catch(error => console.error('Failed to fetch industries:', error));
      }
    }
  }, [show, selectedIndustryIds, allIndustries.length]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onClose, buttonRef]);

  const handleCheckboxChange = (industryId: number) => {
    const newSelection = currentSelection.includes(industryId)
      ? currentSelection.filter(id => id !== industryId)
      : [...currentSelection, industryId];
    
    setCurrentSelection(newSelection);
    
    // 業界選択時に自動的に検索を実行
    onApply(newSelection);
  };

  const handleApply = () => {
    onApply(currentSelection);
    onClose();
  };

  const filteredIndustries = allIndustries.filter(industry => 
    industry.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!show) return null;

  return (
    <div 
      ref={dropdownRef}
      className="position-absolute bg-white border rounded shadow-lg" 
      style={{ 
        top: '100%', 
        left: 0, 
        right: 0, 
        zIndex: 1000,
        maxHeight: '400px',
        overflowY: 'auto',
        minWidth: '500px'
      }}
    >
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">業界で絞り込み</h6>
          <button type="button" className="btn-close" onClick={onClose}></button>
        </div>
        
        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="業界を検索..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="row">
          {filteredIndustries.map(industry => (
            <div key={industry.id} className="col-6 col-md-4">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`industry-${industry.id}`}
                  checked={currentSelection.includes(industry.id)}
                  onChange={() => handleCheckboxChange(industry.id)}
                />
                <label className="form-check-label" htmlFor={`industry-${industry.id}`}>
                  {industry.name}
                </label>
              </div>
            </div>
          ))}
        </div>
        
        <div className="d-flex gap-2 mt-3">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
            キャンセル
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleApply}>
            この条件で検索
          </button>
        </div>
      </div>
    </div>
  );
};

export default IndustriesFilterDropdown;
